import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Package, LayoutDashboard, FileText, Users, ShoppingCart, BarChart3, CreditCard, Menu, ExternalLink, LogOut, Settings, UserCircle, Code, Rocket, Crown, HelpCircle, Wand2 } from 'lucide-react';
import { AIChatWidget } from './AIChatWidget';
import OnboardingTutorial, { hasCompletedOnboarding } from './OnboardingTutorial';

const navItems = [
  { label: 'Overview', icon: LayoutDashboard, path: '/app', exact: true },
  { label: 'Profile', icon: UserCircle, path: '/app/profile', testId: 'sidebar-nav-profile' },
  { label: 'Getting Started', icon: Rocket, path: '/app/getting-started', testId: 'sidebar-nav-getting-started' },
  { section: 'Manage' },
  { label: 'Digital Studio', icon: Wand2, path: '/app/studio', testId: 'sidebar-nav-studio' },
  { label: 'Products Library', icon: Package, path: '/app/products', testId: 'sidebar-nav-products' },
  { label: 'Landing Pages', icon: FileText, path: '/app/landing-pages', testId: 'sidebar-nav-landing-pages' },
  { label: 'Checkout Forms', icon: Code, path: '/app/checkout-forms', testId: 'sidebar-nav-checkout-forms' },
  { section: 'Audience' },
  { label: 'Customers', icon: Users, path: '/app/customers', testId: 'sidebar-nav-customers' },
  { label: 'Orders', icon: ShoppingCart, path: '/app/orders', testId: 'sidebar-nav-orders' },
  { section: 'Insights' },
  { label: 'Sales Report', icon: BarChart3, path: '/app/sales-report', testId: 'sidebar-nav-sales-report' },
  { section: 'Settings' },
  { label: 'Payment Settings', icon: CreditCard, path: '/app/payment-settings', testId: 'sidebar-nav-payment-settings' },
  { label: 'Plan & Upgrade', icon: Crown, path: '/app/plan', testId: 'sidebar-nav-plan' },
  { label: 'User Settings', icon: Settings, path: '/app/user-settings', testId: 'sidebar-nav-user-settings' },
];

function SidebarNav({ onNavClick }) {
  const { user } = useAuth();
  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: '#195259' }}>
      <div className="p-6 pb-4">
        <img src="/logos/funnely-white.png" alt="Funnely" className="h-7 object-contain object-left" />
        <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Creator Dashboard</p>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto" data-testid="dashboard-sidebar">
        {navItems.map((item, i) => {
          if (item.section) {
            return <p key={i} className="text-[10px] uppercase tracking-wider font-semibold px-3 pt-4 pb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{item.section}</p>;
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={onNavClick}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${isActive ? 'text-white' : 'hover:bg-white/10'}`
              }
              style={({ isActive }) => isActive ? { backgroundColor: '#FF9A00', color: '#195259' } : { color: 'rgba(255,255,255,0.7)' }}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
              {item.badge && <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ml-auto" style={{backgroundColor:'#FF9A00',color:'white'}}>{item.badge}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs font-bold" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>{(user?.username || user?.name)?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-white">{user?.username || user?.name}</p>
            <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // Auto-open tutorial once for first-time users
  useEffect(() => {
    if (user && !user.is_admin && !hasCompletedOnboarding()) {
      const t = setTimeout(() => setTutorialOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-[280px] flex-col shrink-0">
        <SidebarNav />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[280px] border-none" style={{ backgroundColor: '#195259' }}>
          <SidebarNav onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 flex items-center justify-between px-4 sm:px-6 shrink-0 bg-white border-b" style={{ borderColor: '#195259' }}>
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
            </Sheet>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTutorialOpen(true)}
                    data-testid="topbar-tutorial-button"
                    aria-label="Replay tutorial"
                  >
                    <HelpCircle className="h-5 w-5" style={{ color: '#195259' }} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Replay tutorial</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/app/landing-pages')}
              data-testid="topbar-preview-storefront-button"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              My Pages
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="topbar-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs font-bold" style={{ backgroundColor: '#195259', color: 'white' }}>{(user?.username || user?.name)?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/app/payment-settings')}>
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* AI Chat Widget */}
      <AIChatWidget />

      {/* Onboarding Tutorial */}
      <OnboardingTutorial open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </div>
  );
}
